import type { TFunction } from "i18next";
import { SearchOutlined } from "@ant-design/icons";

import { iconMap, labelMap } from "tokens/layout";
import type { AppSearchItemData, ItemData } from "types/layout";

import type { SearchEntry } from "./types";

// Side-menu entries that must open in a new browser tab rather than navigate in place.
const OPEN_IN_NEW_TAB_IDS = new Set([
  "sidepanel_knowledge_base",
  "sidepanel_advanced_accounting_portal",
  "sidepanel_customer_portal_demo",
]);

const moduleNameFromItemId = (id: string): string =>
  id.includes("__") ? id.split("__")[1] : id.replace(/^sidepanel_/, "");

const translatedText = (t: TFunction, key?: string): string => (key ? t(key) : "");

// Recursively flattens a side-menu node (and its children) into search entries:
// one "navigation" entry for the node's own link, one "create" entry for its new-entity link.
const menuEntriesFromItem = (
  rawItem: ItemData,
  t: TFunction,
  ancestors: string[] = [],
): SearchEntry[] => {
  if (!rawItem?.props?.id) return [];

  const id = rawItem.props.id as string;
  const labelKey = labelMap.get(id);
  if (!labelKey) return [];

  const label = translatedText(t, labelKey);
  const group = ancestors.join(" / ") || undefined;
  const topGroup = ancestors[0];
  const moduleName = moduleNameFromItemId(id);
  const icon = iconMap.get(id) ?? <SearchOutlined />;
  const entries: SearchEntry[] = [];

  if (rawItem.props.href && rawItem.props.href !== "#") {
    entries.push({
      id,
      label,
      href: rawItem.props.href,
      group,
      topGroup,
      kind: "navigation",
      icon,
      keywords: [labelKey, moduleName],
      mobileModule: moduleName,
      openInNewTab: OPEN_IN_NEW_TAB_IDS.has(id),
    });
  }

  if (rawItem.new_entity?.allowed && rawItem.new_entity.link) {
    entries.push({
      id: `${id}__new`,
      label: t("frontend.app_search.new_item", { item: label }),
      href: rawItem.new_entity.link,
      group: label,
      topGroup: topGroup ?? label,
      kind: "create",
      icon,
      keywords: [label, labelKey, moduleName, `new ${moduleName}`, `create ${moduleName}`],
      mobileModule: `new_${moduleName}`,
      openInNewTab: false,
    });
  }

  rawItem.children?.forEach((child) => {
    entries.push(...menuEntriesFromItem(child, t, [...ancestors, label]));
  });

  return entries;
};

const explicitEntryFromData = (item: AppSearchItemData, t: TFunction): SearchEntry | null => {
  if (!item.href) return null;

  const group = translatedText(t, item.group);

  return {
    id: item.id,
    label: translatedText(t, item.label),
    href: item.href,
    group,
    topGroup: group || undefined,
    kind: item.kind ?? "navigation",
    icon: iconMap.get(item.icon_id ?? item.id) ?? <SearchOutlined />,
    keywords: item.keywords ?? [],
    mobileModule: item.mobile_module ?? "",
    openInNewTab: Boolean(item.open_in_new_tab),
  };
};

// Flattens the side-menu tree plus the explicit search items into a de-duplicated list.
// Explicit items win over menu items sharing the same id (preserving original precedence).
export const buildSearchEntries = (
  sideMenuData: ItemData[] = [],
  appSearchItems: AppSearchItemData[] = [],
  t: TFunction,
): SearchEntry[] => {
  const menuEntries = sideMenuData.reduce<SearchEntry[]>(
    (items, item) => items.concat(menuEntriesFromItem(item, t)),
    [],
  );
  const explicitEntries = appSearchItems
    .map((item) => explicitEntryFromData(item, t))
    .filter((item): item is SearchEntry => Boolean(item));

  const uniqueEntries = new Map<string, SearchEntry>();
  [...menuEntries, ...explicitEntries].forEach((entry) => {
    uniqueEntries.set(entry.id, entry);
  });

  return Array.from(uniqueEntries.values());
};

// Resolves persisted recent-entry ids against the live index, dropping ids that
// no longer exist (renamed modules, revoked permissions) while preserving order.
export const resolveRecentEntries = (
  entries: SearchEntry[],
  recentIds: string[],
): SearchEntry[] => {
  const byId = new Map(entries.map((entry) => [entry.id, entry]));

  return recentIds
    .map((id) => byId.get(id))
    .filter((entry): entry is SearchEntry => Boolean(entry));
};
